import { NextRequest } from "next/server";

type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  event: string;
  message?: string;
  context: {
    source: "api-route" | "worker" | "service" | "cron" | "cdc" | "middleware";
    module?: string;
    function?: string;
    requestId?: string;
  };
  actor?: {
    userId?: string;
    userEmail?: string;
    userWorkosId?: string;
    organizationId?: string;
    organizationSlug?: string;
    impersonatorId?: string;
  };
  request?: {
    method?: string;
    path?: string;
    userAgent?: string;
    ip?: string;
  };
  data?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
    metadata?: Record<string, unknown>;
  };
  performance?: {
    durationMs?: number;
    memoryUsageMB?: number;
  };
}

interface LogOptions {
  context?: Partial<LogEntry["context"]>;
  actor?: LogEntry["actor"];
  request?: LogEntry["request"];
  data?: Record<string, unknown>;
  performance?: LogEntry["performance"];
  message?: string;
}

class Logger {
  private defaultContext: Partial<LogEntry["context"]> = {};
  private actorContext?: LogEntry["actor"];
  private requestContext?: LogEntry["request"];
  private requestId?: string;

  private log(
    level: LogLevel,
    event: string,
    options?: LogOptions & { error?: Error | unknown }
  ): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      event,
      context: {
        source: this.defaultContext.source || "service",
        ...this.defaultContext,
        ...options?.context,
        ...(this.requestId && { requestId: this.requestId }),
      },
      ...(options?.message && { message: options.message }),
      ...(this.actorContext && { actor: this.actorContext }),
      ...(options?.actor && {
        actor: { ...this.actorContext, ...options.actor },
      }),
      ...(this.requestContext && { request: this.requestContext }),
      ...(options?.request && {
        request: { ...this.requestContext, ...options.request },
      }),
      ...(options?.data && { data: options.data }),
      ...(options?.performance && { performance: options.performance }),
    };

    if (options?.error) {
      const error = options.error;
      logEntry.error = {
        name: error instanceof Error ? error.name : "Error",
        message: error instanceof Error ? error.message : String(error),
        ...(error instanceof Error && error.stack && { stack: error.stack }),
      };
    }

    const logMethod =
      level === "error" || level === "fatal" ? console.error : console.log;

    // Pretty-print in development for readability, compact JSON in production for parsing
    const isDevelopment = process.env.NODE_ENV === "development";
    const logOutput = isDevelopment
      ? JSON.stringify(logEntry, null, 2) // Pretty-printed with 2-space indentation
      : JSON.stringify(logEntry); // Compact single-line JSON

    logMethod(logOutput);
  }

  debug(event: string, options?: LogOptions): void {
    this.log("debug", event, options);
  }

  info(event: string, options?: LogOptions): void {
    this.log("info", event, options);
  }

  warn(event: string, options?: LogOptions): void {
    this.log("warn", event, options);
  }

  error(event: string, error?: Error | unknown, options?: LogOptions): void {
    this.log("error", event, { ...options, error });
  }

  fatal(event: string, error?: Error | unknown, options?: LogOptions): void {
    this.log("fatal", event, { ...options, error });
  }

  child(context: Partial<LogEntry["context"]>): Logger {
    const childLogger = new Logger();
    childLogger.defaultContext = { ...this.defaultContext, ...context };
    childLogger.actorContext = this.actorContext;
    childLogger.requestContext = this.requestContext;
    childLogger.requestId = this.requestId;
    return childLogger;
  }

  withRequest(request: NextRequest): Logger {
    const newLogger = new Logger();
    newLogger.defaultContext = { ...this.defaultContext };
    newLogger.actorContext = this.actorContext;
    newLogger.requestId = this.requestId;
    newLogger.requestContext = {
      method: request.method,
      path: request.nextUrl.pathname,
      userAgent: request.headers.get("user-agent") || undefined,
      ip:
        request.headers.get("x-forwarded-for") ||
        request.headers.get("x-real-ip") ||
        undefined,
    };
    return newLogger;
  }

  withActor(actor: LogEntry["actor"]): Logger {
    const newLogger = new Logger();
    newLogger.defaultContext = { ...this.defaultContext };
    newLogger.actorContext = { ...this.actorContext, ...actor };
    newLogger.requestContext = this.requestContext;
    newLogger.requestId = this.requestId;
    return newLogger;
  }

  withRequestId(requestId: string): Logger {
    const newLogger = new Logger();
    newLogger.defaultContext = { ...this.defaultContext };
    newLogger.actorContext = this.actorContext;
    newLogger.requestContext = this.requestContext;
    newLogger.requestId = requestId;
    return newLogger;
  }

  withModule(module: string): Logger {
    return this.child({ module });
  }

  withSource(source: LogEntry["context"]["source"]): Logger {
    return this.child({ source });
  }

  startTimer(): () => number {
    const start = performance.now();
    return (): number => {
      const duration = Math.round(performance.now() - start);
      return duration;
    };
  }
}

export const logger = new Logger();
export const { debug, info, warn, error, fatal } = logger;
